import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import * as enTranslations from '../i18n/en.json';
import * as swTranslations from '../i18n/sw.json';

export type SupportedLang = 'sw' | 'en';

type TranslationMap = { [key: string]: string };

const TRANSLATIONS: { [lang in SupportedLang]: TranslationMap } = {
  en: enTranslations as any,
  sw: swTranslations as any,
};

@Injectable()
export class LanguageService {

  private readonly STORAGE_KEY = 'bao_lang';
  private readonly COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

  private langSubject = new BehaviorSubject<SupportedLang>(
    this.readLang()
  );

  currentLang$ = this.langSubject.asObservable();

  get currentLang(): SupportedLang {
    return this.langSubject.getValue();
  }

  switchLanguage(lang: SupportedLang): void {
    localStorage.setItem(this.STORAGE_KEY, lang);
    this.setCookie(lang);
    this.langSubject.next(lang);
  }

  t(key: string): string {
    const map = TRANSLATIONS[this.currentLang];
    return map[key] !== undefined ? map[key] : key;
  }

  private readLang(): SupportedLang {
    const cookie = this.getCookie();
    if (cookie) {
      return cookie;
    }
    return (localStorage.getItem(this.STORAGE_KEY) as SupportedLang) || 'sw';
  }

  private getCookie(): SupportedLang | null {
    const match = document.cookie
      .split(';')
      .map(c => c.trim())
      .find(c => c.startsWith(this.STORAGE_KEY + '='));
    if (!match) {
      return null;
    }
    const value = match.split('=')[1] as SupportedLang;
    return value === 'en' || value === 'sw' ? value : null;
  }

  private setCookie(lang: SupportedLang): void {
    document.cookie =
      `${this.STORAGE_KEY}=${lang}; path=/; max-age=${this.COOKIE_MAX_AGE}; SameSite=Lax`;
  }
}
